const SEARCH_DEBOUNCE_MS = 150;

class FaqAccordion extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-faqa-card]')];
    if (this.cards.length === 0) return;

    this.pills = [...this.querySelectorAll('[data-faqa-pill]')];
    this.input = this.querySelector('[data-faqa-search]');
    this.empty = this.querySelector('[data-faqa-empty]');
    this.list = this.querySelector('[data-faqa-list]');

    const selectedPill = this.pills.find((pill) => pill.getAttribute('aria-selected') === 'true');
    this.activeCategory = selectedPill?.dataset.faqaCategory ?? '';

    this.controller = new AbortController();
    const { signal } = this.controller;

    if (this.input) {
      this.input.addEventListener(
        'input',
        () => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => this.applyFilter(), SEARCH_DEBOUNCE_MS);
        },
        { signal },
      );
    }

    for (const pill of this.pills) {
      pill.addEventListener('click', () => this.activateCategory(pill.dataset.faqaCategory), { signal });
    }

    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });

    this.applyFilter();
  }

  disconnectedCallback() {
    clearTimeout(this.debounceTimer);
    this.controller?.abort();
  }

  onBlockSelect(event) {
    if (!this.contains(event.target)) return;

    const card = this.cards.find((item) => item.dataset.faqaBlockId === event.detail?.blockId);
    if (!card) return;

    this.activateCategory(card.dataset.faqaCategory);
  }

  activateCategory(category) {
    this.activeCategory = category ?? '';

    if (this.input) this.input.value = '';

    this.applyFilter();
  }

  // A search spans every category, so while one is active no pill may claim to be
  // filtering the results — otherwise the highlighted pill contradicts what's shown.
  syncPills(query) {
    let selectedPill = null;

    for (const pill of this.pills) {
      const isSelected = !query && pill.dataset.faqaCategory === this.activeCategory;
      pill.setAttribute('aria-selected', String(isSelected));
      if (isSelected) selectedPill = pill;
    }

    if (!this.list) return;

    if (selectedPill?.id) {
      this.list.setAttribute('aria-labelledby', selectedPill.id);
    } else {
      this.list.removeAttribute('aria-labelledby');
    }
  }

  // Closing through accordion.js's own handler keeps aria state, focus trapping,
  // icon rotation and max-height in one place rather than forking that logic here.
  collapseOpenCards() {
    for (const trigger of this.querySelectorAll('[data-accordion-trigger][aria-expanded="true"]')) {
      trigger.click();
    }
  }

  applyFilter() {
    const query = this.input?.value.trim().toLowerCase() ?? '';

    this.collapseOpenCards();
    this.syncPills(query);

    let visibleCount = 0;

    for (const card of this.cards) {
      const { faqaCategory, faqaSearchText } = card.dataset;
      // A query deliberately ignores the active pill so search spans every category.
      const isMatch = query
        ? faqaSearchText.includes(query)
        : !this.activeCategory || faqaCategory === this.activeCategory;

      card.hidden = !isMatch;
      if (isMatch) visibleCount += 1;
    }

    if (this.empty) this.empty.hidden = visibleCount > 0;
  }
}

if (!customElements.get('faq-accordion')) {
  customElements.define('faq-accordion', FaqAccordion);
}
